// frontend/src/components/useQBCustomerOptions.js
import { useMemo, useCallback } from 'react';

/**
 * Hook to build hierarchical QB customer options and map once for reuse across multiple selects.
 * This avoids redundant processing when rendering multiple QBCustomerSelect components.
 * 
 * @param {Array} qbCustomers - Array of QB customer objects with id, name, parent_id, level
 * @returns {Object} { hierarchicalOptions, optionMap, buildHierarchicalOptions }
 */
export const useQBCustomerOptions = (qbCustomers) => {
  // Ensure qbCustomers is always an array
  const customers = useMemo(() => {
    return Array.isArray(qbCustomers) ? qbCustomers : [];
  }, [qbCustomers]);
  
  // Build hierarchical options with proper indentation and disable parent nodes
  const buildHierarchicalOptions = useCallback((customers) => {
    if (!customers || customers.length === 0) {
      return [];
    }
    
    // Build tree structure - create copies to avoid mutating original data
    const itemMap = {};
    const roots = [];

    customers.forEach(customer => {
      itemMap[customer.id] = { ...customer, children: [] };
    });

    customers.forEach(customer => {
      if (!customer.parent_id) {
        roots.push(itemMap[customer.id]);
      } else {
        const parent = itemMap[customer.parent_id];
        if (parent) {
          parent.children.push(itemMap[customer.id]);
        } else {
          // Orphaned item (parent not found), treat as root
          roots.push(itemMap[customer.id]);
        }
      }
    });

    // Flatten with depth and disabled flag
    const flattenTree = (item, depth = 0, parentPath = []) => {
      // Determine if this is a leaf node
      // A node is a leaf if it has no children OR if is_leaf is explicitly set to true
      const hasChildren = item.children && item.children.length > 0;
      const isLeaf = !hasChildren || item.is_leaf === true;
      
      const indent = '  '.repeat(depth); // Use standard spaces for indentation
      const prefix = depth > 0 ? '└─ ' : '';
      
      // Build full path for searching (includes all parent names)
      const fullPath = [...parentPath, item.name].join(' ');
      
      // Build display label for selected value (with greyed parent path)
      const displayLabel = parentPath.length > 0 
        ? `${parentPath.join(' › ')} › ${item.name}`
        : item.name;
      
      const result = [{
        value: item.id,
        label: `${indent}${prefix}${item.name}`,
        displayLabel: displayLabel,
        parentPath: parentPath,
        itemName: item.name,
        parentId: item.parent_id, // Store parent_id for efficient ancestor lookup
        disabled: !isLeaf,
        searchLabel: item.name,
        fullPath: fullPath,
        item: item
      }];

      if (hasChildren) {
        item.children.forEach(child => {
          result.push(...flattenTree(child, depth + 1, [...parentPath, item.name]));
        });
      }

      return result;
    };

    roots.sort((a, b) => a.name.localeCompare(b.name));
    return roots.flatMap(root => flattenTree(root));
  }, []);

  const hierarchicalOptions = useMemo(() => buildHierarchicalOptions(customers), [customers, buildHierarchicalOptions]);

  // Build a map of option ID to option for efficient lookups
  const optionMap = useMemo(() => {
    const map = new Map();
    hierarchicalOptions.forEach(option => {
      map.set(option.value, option);
    });
    return map;
  }, [hierarchicalOptions]);

  return {
    hierarchicalOptions,
    optionMap,
    buildHierarchicalOptions
  };
};
